import React, { useEffect, useMemo, useRef, useState } from 'react';

import { AssistenteStyle, Container } from './ResumoViewer.styles';
import { json, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Popper,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { gerarTextoIa, updateResumo } from '../../../services/resumo';
import { toast } from 'react-toastify';
import { set } from 'react-ga';
import { getAtendimentoById } from '../../../services/atendimento';
import ReactQuill from 'react-quill';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CancelIcon from '@mui/icons-material/Cancel';
import ReviewsIcon from '@mui/icons-material/Reviews';
import ChatIA from '../../../components/ChatIA/ChatIA';
import { Autorenew } from '@mui/icons-material';

function ResumoViewer() {
    const { setAtendimento: setAtendimentoContext, tiposResumos } = useOutletContext();
    const navigate = useNavigate();
    const theme = useTheme();
    const params = useParams();
    const timeout = useRef(null);

    const [loadingAtendimento, setLoadingAtendimento] = useState(true);
    const [atendimento, setAtendimento] = useState(null);
    const [blocosResumos, setBlocosResumos] = useState([]);
    const [htmlResumo, setHtmlResumo] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const popperIsOpen = Boolean(anchorEl);
    const [loading, setLoading] = useState(false);
    const [chatIaAberto, setChatIaAberto] = useState(false);
    const caretPosition = useRef({ posicao: 0, blocoIndex: 0 });

    useEffect(() => {
        atualizaAtendimento(params.id);
    }, [params.id]);

    const resumoSelecionado = useMemo(() => {
        if (!atendimento) return null;
        return atendimento?.resumos?.find((resumo) => resumo.id === params.idResumo) || null;
    }, [atendimento, params.idResumo]);

    const tipoResumoSelecionado = useMemo(() => {
        if (resumoSelecionado === null) return null;
        const tipoResumoSelecionado = resumoSelecionado.tipo;
        return tiposResumos.find((tipo) => tipo.value === tipoResumoSelecionado);
    }, [tiposResumos, resumoSelecionado]);

    useEffect(() => {
        if (resumoSelecionado) {
            try {
                const jsonResumo = JSON.parse(resumoSelecionado.json);
                setBlocosResumos(jsonResumo?.blocos.map((bloco) => ({ titulo: bloco.titulo, texto: bloco.resultado })) || []);
            } catch (e) {
                console.error(e);
            }
            setHtmlResumo(resumoSelecionado.texto);
        }
    }, [resumoSelecionado]);

    useEffect(() => {
        console.log(atendimento, blocosResumos);
        if (chatIaAberto) setChatIaAberto(false);
    }, [atendimento]);

    function copiarTexto(texto) {
        navigator.clipboard.writeText(texto).then(
            function () {
                toast.success('Texto copiado');
            },
            function (err) {
                toast.error('Erro ao copiar texto');
            },
        );
    }

    function save(texto, index, imediato = false, loading = false) {
        clearTimeout(timeout.current);
        if (imediato) {
            setBlocosResumos((prev) => {
                const newBlocos = [...prev];
                newBlocos[index].texto = texto;
                updateResumoRequest(resumoSelecionado, newBlocos, loading);
                return newBlocos;
            });
        } else {
            timeout.current = setTimeout(function () {
                setBlocosResumos((prev) => {
                    const newBlocos = [...prev];
                    newBlocos[index].texto = texto;
                    updateResumoRequest(resumoSelecionado, newBlocos);
                    return newBlocos;
                });
            }, 2000);
        }
    }

    async function updateResumoRequest(resumo, blocos, loading = false) {
        try {
            if (loading) setLoading(true);
            console.log('c% Resumo atualizando', 'color: #f00');
            const resumoResponse = await updateResumo({
                ...resumo,
                json: blocos.length > 0 ? JSON.stringify({ informacoes: blocos }) : '',
            });
            const bloco = document.getElementById(`bloco-${caretPosition.current.blocoIndex}`);
            if (bloco) {
                posicionarCursor(caretPosition.current.posicao, `bloco-${caretPosition.current.blocoIndex}`);
            }
            console.log('Resumo atualizado', resumoResponse);
        } catch (error) {
            toast.error('Erro ao salvar resumo');
            console.error(error);
        } finally {
            if (loading) setLoading(false);
        }
    }

    function geraTextoResumo(blocosResumos) {
        let texto = '';
        blocosResumos.forEach((bloco) => {
            texto += `${bloco.titulo}\n\n${bloco.texto}\n\n\n`;
        });
        return texto;
    }

    async function atualizaAtendimento(idAtendimento) {
        try {
            setLoadingAtendimento(true);
            const atendimentoAtualizado = await getAtendimentoById(idAtendimento);
            setAtendimento(atendimentoAtualizado);
            setAtendimentoContext(atendimentoAtualizado);
        } catch (error) {
            console.error(error);
            toast.error('Erro ao carregar atendimento.');
            if (error.response && error.response.status === 403) {
                navigate('/main');
            } else if (error.response && error.response.status === 404) {
                toast.error('Atendimento não encontrado.');
                navigate('/main');
            } else {
                toast.error('Erro ao carregar atendimento. Tente novamente mais tarde.');
            }
        } finally {
            setLoadingAtendimento(false);
        }
    }

    function posicionarCursor(posicao, id) {
        const div = document.getElementById(id);
        const texto = div.firstChild;

        if (!texto || texto.nodeType !== Node.TEXT_NODE) return;

        const range = document.createRange();
        range.setStart(texto, posicao);
        range.setEnd(texto, posicao);

        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        div.focus();
    }

    function retornaPosicaoCaret() {
        const sel = window.getSelection();
        if (sel.rangeCount === 0) return;

        const range = sel.getRangeAt(0);
        const pos = range.startOffset;
        return pos;
    }

    if (loadingAtendimento) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!atendimento) {
        return (
            <Container>
                <Typography variant="h6" textAlign={'center'}>
                    Nenhum atendimento encontrado
                </Typography>
                <Box display={'flex'} justifyContent="center" mt={2}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => window.location.reload()}
                        sx={{ textTransform: 'none' }}
                    >
                        Atualizar Pagina
                    </Button>
                </Box>
            </Container>
        );
    }

    if (!resumoSelecionado) {
        return (
            <Container>
                <Typography variant="h6">Nenhum resumo gerado</Typography>
            </Container>
        );
    }

    return (
        <Container>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    borderRadius: '4px',
                    position: 'relative',
                    // height: '40px',
                    backgroundColor: '#f5f5f5',
                    padding: '8px 10px',
                    border: `1px solid #e7e7e7`,
                }}
            >
                <Box>
                    <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        sx={{
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                        }}
                        onClick={() => {
                            if (atendimento?.resumos.length > atendimento.limiteResumos)
                                return toast.warning(
                                    'Limite de resumos atingido. Não é possível gerar um novo resumo. Caso esteja com dúvidas, entre em contato com o suporte.',
                                );
                            navigate(`/main/${atendimento.id}/${resumoSelecionado.tipo}/gerandoResumo`);
                        }}
                        startIcon={<Autorenew />}
                    >
                        Regerar Resumo
                    </Button>
                </Box>

                <Box
                    display={'flex'}
                    flex={1}
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    position={'absolute'}
                    right={'10px'}
                >
                    {!chatIaAberto && (
                        <Button
                            variant="text"
                            size="small"
                            color="error"
                            sx={{
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                borderRight: `2px solid ${theme.palette.primary.main}`,
                                paddingRight: '10px',
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0,
                            }}
                            startIcon={<ContentCopyIcon />}
                            onClick={() => copiarTexto(geraTextoResumo(blocosResumos))}
                        >
                            Copiar Tudo
                        </Button>
                    )}
                    <Button
                        size="small"
                        color="primary"
                        onClick={() => setChatIaAberto((old) => !old)}
                        sx={{
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                        }}
                        startIcon={<ReviewsIcon />}
                        variant="contained"
                    >
                        {chatIaAberto ? 'Fechar Chat' : 'Conversar Com Seu R1'}
                    </Button>
                </Box>
            </Box>
            <Box>
                <Typography
                    variant="body2"
                    textAlign={'center'}
                    fontWeight={600}
                    textTransform={'uppercase'}
                    width={'100%'}
                    fontSize={'1rem'}
                    sx={{ padding: '10px 0px' }}
                    color={theme.palette.primary.dark}
                >
                    {tipoResumoSelecionado?.name}
                </Typography>
            </Box>
            <Box display={'flex'} flex={1} height={'calc(100% - 70px)'} sx={{ paddingBottom: 3 }}>
                {blocosResumos ? (
                    <Box flex={1} sx={{ overflowY: 'auto', overflowX: 'hidden', marginRight: 2 }} height={'100%'}>
                        {blocosResumos.map((bloco, index) => (
                            <AssistenteStyle key={`${index}-${bloco.titulo}`}>
                                <div className="containerTituloBloco">
                                    <h3 style={{ color: theme.palette.primary.light }}>{bloco.titulo}</h3>
                                    <Box display={'flex'} gap={0.5}>
                                        <Tooltip title="Editar usando IA">
                                            <Button
                                                size="small"
                                                aria-label="Editar usando IA"
                                                color="error"
                                                sx={{ fontSize: '0.7rem' }}
                                                onClick={(event) =>
                                                    setAnchorEl(anchorEl ? null : { target: event.currentTarget, bloco, index })
                                                }
                                                startIcon={<AutoFixHighIcon />}
                                            >
                                                Editar com IA
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Copiar Bloco">
                                            <Button
                                                size="small"
                                                color="error"
                                                aria-label="Copiar"
                                                sx={{ fontSize: '0.7rem' }}
                                                onClick={() => {
                                                    const texto = `${bloco.titulo}\n \n${bloco.texto}`;
                                                    copiarTexto(texto);
                                                }}
                                                startIcon={<ContentCopyIcon />}
                                                variant="text"
                                            >
                                                Copiar Bloco
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                </div>
                                <div className="containerTextArea">
                                    <div
                                        id={`bloco-${index}`}
                                        contenteditable="true"
                                        onKeyUp={(e) => {
                                            caretPosition.current = {
                                                posicao: retornaPosicaoCaret(),
                                                blocoIndex: index,
                                            };
                                            save(e.target.innerText, index);
                                        }}
                                    >
                                        {bloco.texto}
                                    </div>
                                </div>
                            </AssistenteStyle>
                        ))}
                    </Box>
                ) : (
                    <Box flex={1} sx={{ overfloY: 'auto', overflowX: 'hidden' }}>
                        <ReactQuill
                            value={htmlResumo}
                            onChange={(e) => {
                                setHtmlResumo(e);
                            }}
                        />
                    </Box>
                )}
                {chatIaAberto && <ChatIA atendimento={atendimento} />}
            </Box>
            <Popper id={'simple-popper'} open={popperIsOpen} anchorEl={anchorEl?.target}>
                <PopperEditarIA anchorEl={anchorEl} setAnchorEl={setAnchorEl} save={save} />
            </Popper>
            <Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Container>
    );
}

export default ResumoViewer;

function PopperEditarIA({ anchorEl, setAnchorEl, save }) {
    const theme = useTheme();
    const [popperContent, setPopperContent] = useState({ textoInstrucao: '', textoGerado: '' });
    const [loading, setLoading] = useState(false);

    async function gerarTextoComIA(instrucoes, textoReferencia) {
        try {
            setLoading(true);
            var textoGerado = await gerarTextoIa(instrucoes, textoReferencia);
            setPopperContent((prev) => ({
                ...prev,
                textoGerado: textoGerado,
            }));
        } catch (error) {
            console.error('Erro ao gerar texto com IA:', error);
            if (error.response && error.response.status === 429) {
                toast.error('Limite de requisições atingido. Tente novamente mais tarde.');
            } else {
                toast.error('Erro ao gerar texto com IA');
            }
            setPopperContent((prev) => ({
                ...prev,
                textoGerado: '',
            }));
        } finally {
            setLoading(false);
        }
    }

    return (
        <Paper elevation={3}>
            <IconButton
                sx={{ position: 'absolute', right: 10, top: 10 }}
                variant="contained"
                color="primary"
                onClick={() => {
                    setAnchorEl(null);
                }}
            >
                <CancelIcon />
            </IconButton>

            <Box width={600} p={2} display={'flex'} flexDirection="column" gap={2}>
                <Typography
                    variant="h6"
                    gutterBottom
                    color={theme.palette.primary.main}
                    fontWeight={600}
                    sx={{ paddingRight: 2 }}
                >
                    Editar "{anchorEl.bloco.titulo}" com IA
                </Typography>
                <Box>
                    <TextField
                        id="outlined-textarea"
                        label="Como você gostaria de editar este resumo?"
                        placeholder="Ex: Crie uma lista com as informações mais importantes"
                        maxRows={3}
                        multiline
                        fullWidth
                        variant="filled"
                        value={popperContent.textoInstrucao}
                        onChange={(e) => {
                            setPopperContent((prev) => ({
                                ...prev,
                                textoInstrucao: e.target.value,
                            }));
                        }}
                        sx={{ marginBottom: 2, scrollbarColor: `${theme.palette.primary.main} transparent` }}
                    />
                </Box>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={100}>
                        <Typography variant="body2" color="textSecondary">
                            Gerando texto com IA, aguarde...
                        </Typography>
                    </Box>
                ) : popperContent.textoGerado ? (
                    <Box>
                        <TextField
                            id="outlined-textarea"
                            label="Texto Gerado pela IA"
                            placeholder="O texto gerado pela IA aparecerá aqui"
                            multiline
                            fullWidth
                            variant="filled"
                            rows={3}
                            value={popperContent.textoGerado}
                            onChange={(e) => {
                                setPopperContent((prev) => ({
                                    ...prev,
                                    textoGerado: e.target.value,
                                }));
                            }}
                            sx={{ marginBottom: 2, scrollbarColor: `${theme.palette.primary.main} transparent` }}
                        />
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="body2" color="textSecondary" gutterBottom fontWeight={600}>
                            Resumo de "{anchorEl.bloco.titulo}" que será editado:
                        </Typography>
                        <Box
                            sx={{ backgroundColor: theme.palette.grey[100], borderRadius: 2 }}
                            maxHeight={80}
                            overflow={'hidden'}
                        >
                            <Typography p={2} variant="body2" color="textSecondary" overflow={'auto'} height={'100%'}>
                                {anchorEl.bloco.texto}
                            </Typography>
                        </Box>
                    </Box>
                )}

                <Box display={'flex'} justifyContent="flex-end" gap={1}>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        loading={loading}
                        loadingPosition="start"
                        onClick={() => {
                            const instrucoes = popperContent.textoInstrucao || '';
                            if (!instrucoes) {
                                toast.error('Por favor, insira as instruções para gerar o texto.');
                                return;
                            }
                            gerarTextoComIA(instrucoes, anchorEl.bloco.texto);
                        }}
                        startIcon={<AutoFixHighIcon />}
                    >
                        Gerar Com IA
                    </Button>
                    {popperContent.textoGerado && (
                        <Button
                            size="small"
                            variant="contained"
                            color="success"
                            sx={{ marginLeft: 1 }}
                            onClick={() => {
                                const texto = popperContent.textoGerado || '';
                                if (!texto) {
                                    toast.error('Por favor, insira um texto para salvar.');
                                    return;
                                }
                                save(texto, anchorEl.index, true, true);
                                setAnchorEl(null);
                            }}
                        >
                            Salvar
                        </Button>
                    )}
                </Box>
            </Box>
        </Paper>
    );
}
